import React, { useState } from 'react';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api, { errMsg } from '@/lib/api';
import { Loader2, KeyRound } from 'lucide-react';

// Blocking screen shown whenever the signed-in user has `must_change_password`.
// They set a new password before reaching anything else (temporary-password flow).
export const ForcePasswordChange = () => {
  const { user, loginWithToken, logout } = useApp();
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (pw.length < 4) { toast.error('Use at least 4 characters.'); return; }
    if (pw !== confirm) { toast.error('The two passwords do not match.'); return; }
    setBusy(true);
    try {
      const r = await api.post('/auth/change-password', { new_password: pw });
      const { token, user: u } = r.data.data;
      await loginWithToken(token, u);
      toast.success('Password updated. Welcome to SHABZ CLOSET.');
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="app-bg fixed inset-0 z-[200] flex min-h-screen flex-col items-center justify-center px-5"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      data-testid="force-password-change">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <KeyRound className="h-7 w-7" />
          </div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Create a new password</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Hi {user?.name || user?.username} — for your security, please set your own password to continue.
          </p>
        </div>
        <form onSubmit={submit} className="rounded-2xl border border-border bg-card p-5 shadow-sm space-y-4">
          <div>
            <Label htmlFor="fpc-new">New password</Label>
            <Input id="fpc-new" type="password" value={pw} onChange={(e) => setPw(e.target.value)}
              className="mt-1 h-11 rounded-xl" placeholder="••••••" autoFocus data-testid="fpc-new-password" />
          </div>
          <div>
            <Label htmlFor="fpc-confirm">Confirm new password</Label>
            <Input id="fpc-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="mt-1 h-11 rounded-xl" placeholder="••••••" data-testid="fpc-confirm-password" />
          </div>
          <Button type="submit" disabled={busy}
            className="h-12 w-full rounded-xl text-base font-semibold" data-testid="fpc-submit">
            {busy ? <span className="flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin" />Saving…</span> : 'Save and continue'}
          </Button>
          <button type="button" onClick={logout} className="w-full text-center text-xs text-muted-foreground underline" data-testid="fpc-signout">
            Sign out instead
          </button>
        </form>
      </div>
    </div>
  );
};
